import { useContext, useEffect, useMemo, useState } from "react";
import { Typography, Grid2 as Grid, Stack, Button } from "@mui/material";
import { billingService } from "../../../Core/Api/services";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import moment from "moment";
import TableSkeleton from "../../../Components/Table/TableSkeleton";
import ListPageHeader, { ListPageHeaderLeft, ListPageHeaderRight } from "../../../Components/Misc/ListPageHeader";
import { GetMySubscriptionResponse } from "../../../Core/Api/Types/get-my-subscription-response";
import BoxPaper from "../../../Components/Misc/BoxPaper";
import MetadataListTextItem from "../../../Components/Misc/Metadata/MetadataListTextItem/metadata-list-text-item";
import { DbInvoiceExpenseInfos } from "../../../Core/Api/Types/db-invoice-expense-infos";
import PageFilter from "../../../Components/Misc/PageFilter";
import PeopleIcon from "@mui/icons-material/People";
import MemoryIcon from "@mui/icons-material/Memory";
import PublicIcon from "@mui/icons-material/Public";
import TaskOutlinedIcon from "@mui/icons-material/TaskOutlined";
import CloudCircleIcon from "@mui/icons-material/CloudCircle";
import CurrencyExchangeIcon from "@mui/icons-material/CurrencyExchange";
import TuneIcon from "@mui/icons-material/Tune";
import CancelIcon from "@mui/icons-material/Cancel";
import { BillingMetricType, BillingMetricsData } from "../../../Core/Api/Types/list-expenses-response";
import BillingExpenseTable from "../Expenses/billing-expenses-table";
import { CurrencyTypes } from "../../../Core/Api/Types/currency-types";
import { ModalContext } from "../../../Components/Modals";
import ModalBillingChangeOffer, {
    ModalBillingChangeOfferElementProps,
} from "../../../Components/Modals/ModalBillingChangeOffer/modal-billing-change-offer";
import ModalBillingChangeLicences, {
    ModalBillingChangeLicencesElementProps,
} from "../../../Components/Modals/ModalBillingChangeLicences/modal-billing-change-licences";
import { useTranslation } from "react-i18next";

const BillingOffer = (props) => {
    const { t } = useTranslation();
    const [subscription, setSubscription] = useState<GetMySubscriptionResponse>();
    const [expenses, setExpenses] = useState<DbInvoiceExpenseInfos[]>([]);
    const [metrics, setMetrics] = useState<BillingMetricsData>();
    const [loading, setLoading] = useState(true);
    const [reload, setReload] = useState(0);

    const modalContext = useContext(ModalContext);
    const { openModal } = { ...modalContext };

    useEffect(() => {
        billingService.GetMySubscription().then((response) => {
            if (response) setSubscription(response);
        });

        billingService.ListCurrentExpenses().then((response) => {
            if (response.expenses) {
                setExpenses(response.expenses);
                setLoading(false);
            }

            if (response.metrics) setMetrics(response.metrics);
        });
    }, [reload]);

    const totalExpenses = useMemo(() => {
        if (expenses.length === 0) return 0;
        return expenses
            .map((x) => x.price)
            .reduce((prev, next) => prev + next)
            .toFixed(2);
    }, [expenses]);

    return (
        <>
            <ListPageHeader mb={2}>
                <ListPageHeaderLeft
                    title={
                        loading
                            ? GlobalConst.LOADING_TEXT
                            : t("component:listPageHeaderLeft.title.mySubscription", {
                                context: subscription?.offer?.name ? "withName" : "",
                                name: subscription?.offer?.name,
                            })
                    }
                />
                <ListPageHeaderRight>
                    <Button
                        onClick={() => {
                            if (!openModal || !subscription?.offer) return;

                            openModal<ModalBillingChangeOfferElementProps>(ModalBillingChangeOffer, {
                                offer: subscription.offer,
                                priceModel: subscription.priceModel,
                                onClose: () => { },
                                onSuccess: () => {
                                    setReload((prev) => prev + 1);
                                },
                            });
                        }}
                        variant="contained"
                        color="primary"
                        startIcon={<CurrencyExchangeIcon />}
                    >
                        {t("button:changePlan")}
                    </Button>
                    <Button
                        onClick={() => {
                            if (!openModal || !subscription?.offer) return;

                            openModal<ModalBillingChangeLicencesElementProps>(ModalBillingChangeLicences, {
                                priceModel: subscription.priceModel,
                                onClose: () => { },
                                onSuccess: () => {
                                    setReload((prev) => prev + 1);
                                },
                            });
                        }}
                        variant="outlined"
                        color="primary"
                        startIcon={<TuneIcon />}
                    >
                        {t("button:manageLicences")}
                    </Button>
                    <Button onClick={() => { }} variant="contained" color="error" startIcon={<CancelIcon />} disabled>
                        {t("button:unsubscribe")}
                    </Button>
                </ListPageHeaderRight>
            </ListPageHeader>

            <Grid container spacing={3}>
                <Grid size={8}>
                    {loading && <TableSkeleton />}
                    {!loading && (
                        <Stack gap={3}>
                            <Grid container spacing={1}>
                                <Grid size={4}>
                                    <PageFilter
                                        variant={
                                            (metrics?.values.find((x) => x.type === BillingMetricType.UsersBasic)?.overdue ?? 0) > 0
                                                ? "warning"
                                                : "info"
                                        }
                                        title={`${metrics?.values.find((x) => x.type === BillingMetricType.UsersBasic)?.consumed} / ${metrics?.values.find((x) => x.type === BillingMetricType.UsersBasic)?.included
                                            }`}
                                        subtitle={t("page:pageFilter.subtitle.basicUsers")}
                                        icon={<PeopleIcon />}
                                    />
                                </Grid>
                                <Grid size={4}>
                                    <PageFilter
                                        variant={
                                            (metrics?.values.find((x) => x.type === BillingMetricType.UsersAdmin)?.overdue ?? 0) > 0
                                                ? "warning"
                                                : "info"
                                        }
                                        title={`${metrics?.values.find((x) => x.type === BillingMetricType.UsersAdmin)?.consumed} / ${metrics?.values.find((x) => x.type === BillingMetricType.UsersAdmin)?.included
                                            }`}
                                        subtitle={t("page:pageFilter.subtitle.adminUsers")}
                                        icon={<PeopleIcon />}
                                    />
                                </Grid>
                                <Grid size={4}>
                                    <PageFilter
                                        variant={
                                            (metrics?.values.find((x) => x.type === BillingMetricType.MediaProcessingMinutes)?.overdue ?? 0) >
                                                0
                                                ? "warning"
                                                : "info"
                                        }
                                        title={`${metrics?.values.find((x) => x.type === BillingMetricType.MediaProcessingMinutes)?.consumed
                                            } / ${metrics?.values.find((x) => x.type === BillingMetricType.MediaProcessingMinutes)?.included}`}
                                        subtitle={t("page:pageFilter.subtitle.mediaProcessingMinutes")}
                                        icon={<MemoryIcon />}
                                    />
                                </Grid>
                                <Grid size={4}>
                                    <PageFilter
                                        variant={
                                            (metrics?.values.find((x) => x.type === BillingMetricType.TransferOutBandwidth)?.overdue ?? 0) > 0
                                                ? "warning"
                                                : "info"
                                        }
                                        title={`${(
                                            (metrics?.values.find((x) => x.type === BillingMetricType.TransferOutBandwidth)?.consumed ?? 0) /
                                            1000 /
                                            1000 /
                                            1000
                                        ).toFixed(2)} / ${(metrics?.values.find((x) => x.type === BillingMetricType.TransferOutBandwidth)?.included ?? 0) /
                                        1000 /
                                        1000 /
                                        1000
                                            }`}
                                        subtitle={t("page:pageFilter.subtitle.dataTransferOut")}
                                        icon={<PublicIcon />}
                                    />
                                </Grid>
                                <Grid size={4}>
                                    <PageFilter
                                        variant={
                                            (metrics?.values.find((x) => x.type === BillingMetricType.StorageDisk)?.overdue ?? 0) > 0
                                                ? "warning"
                                                : "info"
                                        }
                                        title={`${(
                                            (metrics?.values.find((x) => x.type === BillingMetricType.StorageDisk)?.consumed ?? 0) /
                                            1000 /
                                            1000 /
                                            1000 /
                                            1000
                                        ).toFixed(2)} / ${(metrics?.values.find((x) => x.type === BillingMetricType.StorageDisk)?.included ?? 0) /
                                        1000 /
                                        1000 /
                                        1000 /
                                        1000
                                            }`}
                                        subtitle={t("page:pageFilter.subtitle.storageDisk")}
                                        icon={<CloudCircleIcon />}
                                    />
                                </Grid>
                                <Grid size={4}>
                                    <PageFilter
                                        variant={
                                            (metrics?.values.find((x) => x.type === BillingMetricType.IngestCountRawVideo)?.overdue ?? 0) > 0
                                                ? "warning"
                                                : "info"
                                        }
                                        title={`${metrics?.values.find((x) => x.type === BillingMetricType.IngestCountRawVideo)?.consumed
                                            } / ${metrics?.values.find((x) => x.type === BillingMetricType.IngestCountRawVideo)?.included}`}
                                        subtitle={t("page:pageFilter.subtitle.ingestOfRawVideo")}
                                        icon={<TaskOutlinedIcon />}
                                    />
                                </Grid>
                            </Grid>

                            <BillingExpenseTable
                                expenses={expenses}
                                currency={subscription?.currency ?? CurrencyTypes.EUR}
                                title={t("page:billingExpenseTable.title.currentExpenses")}
                            />
                        </Stack>
                    )}
                </Grid>
                <Grid size={4}>
                    <BoxPaper title={t("page:boxPaper.title.subscriptionSummary")} mb={2}>
                        {!loading && (
                            <Stack spacing={1}>
                                <MetadataListTextItem title={t("menu:title.billingPeriod")}>
                                    {moment().format("MMMM YYYY")}
                                </MetadataListTextItem>
                                <MetadataListTextItem title={t("menu:title.planName")}>
                                    {subscription?.offer?.name ?? ""}
                                </MetadataListTextItem>

                                <Stack>
                                    <Typography variant="h6" mt={2} mb={2} color="text.secondary">
                                        {t("page:body.licensing")}
                                    </Typography>

                                    <Stack spacing={1}>
                                        {subscription?.licences?.map((lic) => (
                                            <MetadataListTextItem title={lic} key={lic} color="text.tier">
                                                {t("common:enabled")}
                                            </MetadataListTextItem>
                                        ))}
                                    </Stack>
                                </Stack>
                            </Stack>
                        )}
                        {loading && <TableSkeleton />}
                    </BoxPaper>

                    <BoxPaper title={t("page:boxPaper.title.currentBill")} mb={2}>
                        {!loading && (
                            <Stack spacing={1}>
                                <MetadataListTextItem title={t("menu:title.cost")}>
                                    {totalExpenses} {subscription?.currency}
                                </MetadataListTextItem>

                                <MetadataListTextItem title={t("menu:title.lastUpdateTime")}>
                                    {moment(subscription?.lastUpdate).format(GlobalConst.MOMENT_DATE_FORMAT_FULL)}
                                </MetadataListTextItem>
                                <MetadataListTextItem title={t("menu:title.periodStart")}>
                                    {moment(subscription?.billingPeriodStart).format(GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS)}
                                </MetadataListTextItem>
                                <MetadataListTextItem title={t("menu:title.periodEnd")}>
                                    {moment(subscription?.billingPeriodEnd).format(GlobalConst.MOMENT_DATE_FORMAT_DATENOHOURS)}
                                </MetadataListTextItem>
                                <MetadataListTextItem title={t("menu:title.expensesTotalNb")}>
                                    {t("menu:item.itemsWithCount", { count: expenses.length })}
                                </MetadataListTextItem>
                                <MetadataListTextItem title={t("menu:title.expensesChargedNb")}>
                                    {t("menu:item.itemsWithCount", { count: expenses.filter((x) => x.price !== 0).length })}
                                </MetadataListTextItem>
                            </Stack>
                        )}
                        {loading && <TableSkeleton />}
                    </BoxPaper>
                </Grid>
            </Grid>
        </>
    );
};

export default BillingOffer;
